"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-border bg-background/40">
        <p className="font-default text-xl text-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-border text-foreground hover:bg-primary hover:text-primary-foreground">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Name
            </FormLabel>
            <FormControl>
              <Input {...field} placeholder="Americo Moscariello" className="h-12 px-0 bg-transparent border-0 border-b border-border font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="company" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Company / Property
            </FormLabel>
            <FormControl>
              <Input {...field} placeholder="Your business or property name" className="h-12 px-0 bg-transparent border-0 border-b border-border font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Email
              </FormLabel>
              <FormControl>
                <Input {...field} type="email" placeholder="contact@moscariellobuilder.com" className="h-12 px-0 bg-transparent border-0 border-b border-border font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Phone
              </FormLabel>
              <FormControl>
                <Input {...field} type="tel" placeholder="610-342-5237" className="h-12 px-0 bg-transparent border-0 border-b border-border font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="inquiry_type" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Service Type
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="h-12 px-0 bg-transparent border-0 border-b border-border font-default text-lg text-foreground focus:border-primary focus:ring-0 data-[placeholder]:text-muted-foreground/60 w-full">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="border-border bg-popover">
                  <SelectItem value="Construction" className="font-default">Construction</SelectItem>
                  <SelectItem value="Property Management" className="font-default">Property Management</SelectItem>
                  <SelectItem value="Consultation" className="font-default">Consultation</SelectItem>
                  <SelectItem value="Other" className="font-default">Other</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Project Details
            </FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Tell us about your construction or property management needs." rows={5} className="px-0 py-3 bg-transparent border-0 border-b border-border font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0 resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="lookbook_signup" render={({
      field
    }) => <FormItem className="flex flex-row items-center gap-3 space-y-0">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground size-5" />
            </FormControl>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground cursor-pointer">
              Keep me updated on company news
            </FormLabel>
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive bg-destructive/10">
          <p className="text-destructive font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="group w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-sm uppercase tracking-widest shadow-lg">
        <span>{isSubmitting ? "Sending..." : "Submit Request"}</span>
        <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1.5" />
      </Button>

      <div className="flex justify-end">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          We respond within 24 hours · Mon–Fri
        </p>
      </div>
    </Form>;
}
export default function Inquiry() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const infoBlocks = [{
    label: "Email",
    value: "contact@moscariellobuilder.com"
  }, {
    label: "Phone",
    value: "610-342-5237"
  }, {
    label: "Office",
    value: "24 Donny Brook Way, Collegeville, PA, 19426"
  }];
  return <section data-section-id="3822" id="lets-discuss-your-vision" className="bg-background text-foreground pt-32 md:pt-48 lg:pt-[200px] pb-24 px-6 md:px-12 lg:px-20 overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-700 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="flex items-center gap-3 mb-8">
              <span className="inline-block size-2 bg-primary" />
              <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Moscariello Construction</span>
            </div>

            <h2 className="font-default font-bold uppercase leading-[0.95] tracking-tight text-foreground mb-10" style={{
            fontSize: "clamp(3rem, 7vw, 7rem)"
          }}>
              Let's
              <br />
              build
              <br />
              together<span className="text-primary">.</span>
            </h2>

            <p className="font-default text-lg md:text-xl leading-relaxed text-muted-foreground max-w-md mb-12">
              Whether you are planning a new construction project or seeking professional property management, our team is ready to assist.
            </p>

            <div className="space-y-0 border-t border-border">
              {infoBlocks.map((block, i) => <div key={i} data-index={i} className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-2 md:gap-6 py-6 border-b border-border">
                  <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground pt-1">
                    {block.label}
                  </span>
                  <span className="font-default text-lg text-foreground">
                    {block.value}
                  </span>
                </div>)}
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 lg:col-start-7 transition-all duration-700 delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="vision_form_connect_with_us" formId="vision_form_connect_with_us" sectionName="lets-discuss-your-vision" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company: "",
            inquiry_type: "",
            message: "",
            lookbook_signup: false
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            inquiry_type: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Submit Request" successMessage="Inquiry received. Our team will respond within 24 hours." errorMessage="Transmission failed. Please try again or email us directly." className="relative bg-card/30 backdrop-blur-xl border border-border p-8 md:p-12 lg:p-16 shadow-4xl">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}